import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  calculateEstimate,
  formatMoney,
  isQuestionVisible,
} from "./estimatePricing";
import "./RoughEstimateBuilder.css";

const EMPTY_CUSTOMER = {
  fullName: "",
  address: "",
  city: "",
  state: "VA",
  zip: "",
  email: "",
  phone: "",
  occupancy: "Owner",
  notes: "",
};


const STATE_OPTIONS = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["DC", "District of Columbia"], ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"],
  ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"],
  ["MD", "Maryland"], ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"],
  ["MS", "Mississippi"], ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"],
  ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"],
  ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"],
  ["SC", "South Carolina"], ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"],
  ["UT", "Utah"], ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"],
  ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
];

const RoughEstimateBuilder = ({ embedded = false }) => {
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [loading, setLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const loadTopics = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "estimateTopics"), orderBy("sortOrder", "asc"))
        );
        setTopics(
          snap.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((topic) => topic.active !== false)
        );
      } catch (err) {
        console.error(err);
        setError("The estimate choices could not be loaded.");
      } finally {
        setLoading(false);
      }
    };
    loadTopics();
  }, []);

  const openTopic = async (topic) => {
    setError("");
    setSelectedTopic(topic);
    setQuestions([]);
    setAnswers({});
    setSubmittedId("");
    setLoadingQuestions(true);

    try {
      const snap = await getDocs(
        query(
          collection(db, "estimateTopics", topic.id, "questions"),
          orderBy("sortOrder", "asc")
        )
      );
      setQuestions(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (err) {
      console.error(err);
      setError("The questions for this service could not be loaded.");
    } finally {
      setLoadingQuestions(false);
    }
  };

  const resetBuilder = () => {
    setSelectedTopic(null);
    setQuestions([]);
    setAnswers({});
    setCustomer(EMPTY_CUSTOMER);
    setSubmittedId("");
    setError("");
  };

  const visibleQuestions = useMemo(
    () => questions.filter((question) => isQuestionVisible(question, answers)),
    [questions, answers]
  );

  const estimate = useMemo(
    () => calculateEstimate({ topic: selectedTopic, questions, answers }),
    [selectedTopic, questions, answers]
  );

  const setAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const resetOptions = () => {
    setAnswers({});
    setSubmittedId("");
    setError("");
  };

  const validate = () => {
    const missingQuestion = visibleQuestions.find(
      (q) => q.required && (answers[q.id] === undefined || answers[q.id] === "" || answers[q.id]?.length === 0)
    );
    if (missingQuestion) return `Please complete: ${missingQuestion.label}`;
    if (!customer.fullName.trim()) return "Please enter your full name.";
    if (!customer.email.trim() && !customer.phone.trim()) return "Please enter an email address or phone number.";
    return "";
  };

  const submitEstimate = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const answerSummary = visibleQuestions.map((question) => {
        const raw = answers[question.id];
        const selectedIds = Array.isArray(raw) ? raw : [raw];
        const selectedLabels = (question.options || [])
          .filter((option) => selectedIds.includes(option.id))
          .map((option) => option.label);

        return {
          questionId: question.id,
          question: question.label,
          type: question.type,
          value: selectedLabels.length ? selectedLabels.join(", ") : raw ?? "",
          rawValue: raw ?? "",
        };
      });

      const docRef = await addDoc(collection(db, "roughEstimateRequests"), {
        topicId: selectedTopic.id,
        topicName: selectedTopic.name,
        customer,
        answers,
        answerSummary,
        estimate,
        status: "New",
        source: "Public Rough Estimate Builder",
        createdAt: serverTimestamp(),
        createdAtText: new Date().toISOString(),
      });

      setSubmittedId(docRef.id);
    } catch (err) {
      console.error(err);
      setError("The request could not be submitted. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="reb-page"><p>Loading estimate choices…</p></div>;

  return (
    <div className="reb-page">
      {embedded && (
        <div className="reb-success" style={{ marginBottom: 16 }}>
          Logged-in portal view. Use the portal tabs above to switch pages.
        </div>
      )}
      <div className="reb-header">
        <div>
          <p className="reb-eyebrow">JMC Contracting Group</p>
          <h1>Rough Project Price Builder</h1>
          <p>Choose a service, answer a few questions, and receive a preliminary project range and material summary.</p>
        </div>
        {selectedTopic && <button className="reb-secondary" onClick={resetBuilder}>← Back to Services</button>}
      </div>

      {error && <div className="reb-alert">{error}</div>}

      {!selectedTopic ? (
        <section>
          <h2>Select a Service</h2>
          <div className="reb-topic-grid">
            {topics.map((topic) => (
              <button key={topic.id} className="reb-topic-card" onClick={() => openTopic(topic)}>
                <div className="reb-card-image-wrap">
                  {topic.imageUrl ? <img src={topic.imageUrl} alt={topic.name} /> : <div className="reb-placeholder">No image</div>}
                </div>
                <strong>{topic.name}</strong>
                {topic.shortDescription && <span>{topic.shortDescription}</span>}
              </button>
            ))}
          </div>
        </section>
      ) : (
        <form onSubmit={submitEstimate}>
          <section className="reb-builder-layout">
            <div className="reb-question-column">
              <div className="reb-section-heading">
                <div>
                  <p className="reb-eyebrow">Selected Service</p>
                  <h2>{selectedTopic.name}</h2>
                </div>
                <button
                  className="reb-secondary"
                  type="button"
                  onClick={resetOptions}
                  disabled={Object.keys(answers).length === 0}
                >
                  Reset Options
                </button>
              </div>

              {loadingQuestions && (
                <div className="reb-question-card">
                  <h3>Loading project options…</h3>
                </div>
              )}

              {!loadingQuestions && questions.length === 0 && (
                <div className="reb-question-card reb-empty-options">
                  <h3>No pricing options have been added for this topic yet.</h3>
                  <p>
                    An administrator must open <strong>Estimate Builder Setup</strong>,
                    select this topic, and add question rows with choices and prices.
                  </p>
                </div>
              )}

              {!loadingQuestions && visibleQuestions.map((question, index) => (
                <div className="reb-question-card" key={question.id}>
                  <div className="reb-question-title">
                    <span>{index + 1}</span>
                    <div>
                      <h3>{question.label}</h3>
                      {question.helpText && <p>{question.helpText}</p>}
                    </div>
                  </div>

                  {(question.type === "singleChoice" || question.type === "multiChoice") && (
                    <div className="reb-option-grid">
                      {(question.options || []).map((option) => {
                        const current = answers[question.id];
                        const selected = current === option.id;

                        return (
                          <button
                            type="button"
                            key={option.id}
                            className={`reb-option-card ${selected ? "selected" : ""}`}
                            onClick={() => setAnswer(question.id, option.id)}
                          >
                            {option.imageUrl && <img src={option.imageUrl} alt={option.label} />}
                            <span className="reb-radio" aria-hidden="true">{selected ? "●" : "○"}</span>
                            <strong>{option.label}</strong>
                            {option.description && <small>{option.description}</small>}
                            {option.priceType !== "none" && Number(option.price || 0) > 0 && (
                              <small className="reb-option-price">
                                {formatMoney(option.price)}
                                {option.priceType === "perUnit" ? " per unit" : ""}
                                {option.priceType === "perSection" ? ` per ${option.sectionLength || selectedTopic.defaultSectionLength || 1}' section` : ""}
                                {option.priceType === "flat" ? " add-on" : ""}
                              </small>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {question.type === "number" && (
                    <div className="reb-inline-field">
                      <input
                        type="number"
                        min={question.min ?? 0}
                        max={question.max ?? undefined}
                        step={question.step || 1}
                        value={answers[question.id] ?? ""}
                        onChange={(e) => setAnswer(question.id, e.target.value)}
                        placeholder={question.placeholder || "Enter a number"}
                      />
                      {question.unitLabel && <span>{question.unitLabel}</span>}
                    </div>
                  )}

                  {(question.type === "text" || question.type === "textarea") && (
                    question.type === "textarea" ? (
                      <textarea
                        rows="4"
                        value={answers[question.id] ?? ""}
                        onChange={(e) => setAnswer(question.id, e.target.value)}
                        placeholder={question.placeholder || "Enter details"}
                      />
                    ) : (
                      <input
                        type="text"
                        value={answers[question.id] ?? ""}
                        onChange={(e) => setAnswer(question.id, e.target.value)}
                        placeholder={question.placeholder || "Enter details"}
                      />
                    )
                  )}
                </div>
              ))}

              {!loadingQuestions && questions.length > 0 && (
              <div className="reb-question-card">
                <h3>Your Information</h3>
                <div className="reb-form-grid">
                  <label>Full Name<input value={customer.fullName} onChange={(e) => setCustomer({ ...customer, fullName: e.target.value })} /></label>
                  <label>Email<input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} /></label>
                  <label>Phone<input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} /></label>
                  <label>Owner or Renter<select value={customer.occupancy} onChange={(e) => setCustomer({ ...customer, occupancy: e.target.value })}><option>Owner</option><option>Renter</option><option>Property Manager</option><option>Association</option></select></label>
                  <label className="reb-full">Street Address<input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} /></label>
                  <label>City<input value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} /></label>
                  <label>
                    State
                    <select
                      value={customer.state}
                      onChange={(e) => setCustomer({ ...customer, state: e.target.value })}
                    >
                      {STATE_OPTIONS.map(([code, name]) => (
                        <option key={code} value={code}>
                          {code} — {name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>ZIP<input value={customer.zip} onChange={(e) => setCustomer({ ...customer, zip: e.target.value })} /></label>
                  <label className="reb-full">Additional Notes<textarea rows="4" value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} /></label>
                </div>
              </div>
              )}
            </div>

            {!loadingQuestions && questions.length > 0 && (
            <aside className="reb-summary">
              <h2>Rough Estimate</h2>
              <div className="reb-total">{formatMoney(estimate.total)}</div>
              <p className="reb-disclaimer">Preliminary planning figure only. Final pricing requires site verification, measurements, permits, access review, and written approval.</p>

              <h3>Price Items</h3>
              {estimate.lineItems.length ? estimate.lineItems.map((item, index) => (
                <div className="reb-summary-row" key={`${item.description}-${index}`}>
                  <span>{item.description}</span><strong>{formatMoney(item.amount)}</strong>
                </div>
              )) : <p>No priced selections yet.</p>}

              <h3>Material List</h3>
              {estimate.materials.length ? (
                <ul className="reb-material-list">
                  {estimate.materials.map((material) => (
                    <li key={`${material.name}-${material.unit}`}><span>{material.name}</span><strong>{material.quantity} {material.unit}</strong></li>
                  ))}
                </ul>
              ) : <p>Materials will appear as choices are selected.</p>}

              <button className="reb-primary" type="submit" disabled={submitting || Boolean(submittedId)}>
                {submittedId ? "Request Submitted" : submitting ? "Submitting…" : "Submit Estimate Request"}
              </button>
              {submittedId && <div className="reb-success">Thank you. Your request was received. Reference: {submittedId.slice(0, 8).toUpperCase()}</div>}
            </aside>
            )}
          </section>
        </form>
      )}
    </div>
  );
};

export default RoughEstimateBuilder;
